import { useRef } from "react";
import { motion, useScroll, useTransform, useMotionValue, useSpring } from "framer-motion";

const EASE = [0.76, 0, 0.24, 1];
// Righe in Title Case con &
const LINES = ["Matter,", "Shape,", "Silence."];

const MaskLine = ({ children, i }) => (
  <span className="block overflow-hidden pb-[0.1em]">
    <motion.span
      className="block"
      initial={{ y: "105%" }}
      animate={{ y: "0%" }}
      transition={{ duration: 1.5, delay: 0.25 + i * 0.16, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const textY = useTransform(scrollYProgress, [0, 1], ["0%", "-40%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotX = useSpring(useTransform(my, [-0.5, 0.5], [4, -4]), { stiffness: 40, damping: 18 });
  const rotY = useSpring(useTransform(mx, [-0.5, 0.5], [-6, 6]), { stiffness: 40, damping: 18 });

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

return (
  <section id="top" ref={ref} onMouseMove={onMove} data-testid="hero-section"
    className="relative min-h-[100svh] overflow-hidden bg-ink flex flex-col justify-between pt-28 md:pt-36 pb-12 md:pb-16" style={{ perspective: 1400 }}>
    
    {/* Immagine di sfondo */}
    <motion.div style={{ y: imgY, rotateX: rotX, rotateY: rotY }} className="absolute inset-[-4%] will-change-transform">
      <motion.img
        src="/alkeon-hero-matter.jpg" 
        alt="ALKEON Raw Mineral and Fossil Texture"
        initial={{ scale: 1.18, opacity: 0 }} 
        animate={{ scale: 1.04, opacity: 1 }}
        transition={{ duration: 2.8, ease: [0.22, 1, 0.36, 1] }}
       className="w-full h-full object-cover object-center scale-x-[-1] filter brightness-85 contrast-105"
      />
    </motion.div>
    <div className="absolute inset-0 hero-vignette" />
    <div className="grain" />

      {/* Contenuto Testuale */}
      <motion.div style={{ y: textY, opacity: fade }}
        className="relative z-10 w-full px-5 md:px-14 lg:px-20 my-auto">
        
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1.2, delay: 1.2 }}
          className="label mb-4 md:mb-6" data-testid="hero-eyebrow">
          Collection I — Silent Fossils
        </motion.p>

        {/* Titolo in Title Case (minuscolo con iniziali maiuscole) */}
        <h1 className="font-serif text-bone text-5xl sm:text-7xl md:text-8xl lg:text-9xl tracking-tight leading-[0.95] font-light" data-testid="hero-title">
          {LINES.map((l, i) => (
            <MaskLine key={l} i={i}>
              <span className={i === 1 ? "italic font-normal text-stone/90 pl-[0.4em]" : ""}>
                {l}
              </span>
            </MaskLine>
          ))}
        </h1>

        <div className="mt-8 md:mt-12 flex flex-col md:flex-row md:items-end md:justify-between gap-8">
          <motion.p initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, delay: 1.25, ease: [0.22, 1, 0.36, 1] }}
            className="body max-w-sm" data-testid="hero-subtitle">
            Artisanal mineral sculptures for spaces of quietude.
          </motion.p>
          <motion.a initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1.2, delay: 1.5 }}
            href="#collection" data-testid="explore-hero-cta" className="quiet-link self-start md:self-auto">
            <span>Explore Collection</span>
            <span className="quiet-link-line" />
          </motion.a>
        </div>
      </motion.div>

      {/* Tasto Scroll */}
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 2.2, duration: 1 }}
        className="absolute right-5 md:right-14 top-1/2 -translate-y-1/2 z-10 hidden sm:flex flex-col items-center gap-4">
        <span className="label [writing-mode:vertical-rl]">Scroll</span>
        <span className="scroll-tick" />
      </motion.div>
    </section>
  );
};

export default Hero;